import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Loader2, Phone, Sparkles, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getTrialWeek, listRoster, markTrialCalled, saveTrialWeekSettings, setTrialCoach, tickTrialStep } from "@/lib/api";
import { numberLocale } from "@/lib/money";

// 1 Week Trial (backend routes/trial_week.py): bought on the public page /try/<gym>, done in the app.
// Each new trial gets the next approved trial coach in turn (skipping anyone on leave); a manager can
// give one by hand when nobody was free. The coach then messages them 1-1 in the app.
// Their price comes back as credit (6WC or 12 weeks+) once they've done all four, or after the week anyway.
// Until the iOS app is approved (2 Nov): the nutrition course online (ticks itself when they pass the test),
// 4 check-ins, and replying on Trainerize, which the coach ticks here (FPA can't see it).

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const fmt = (n) => new Intl.NumberFormat(numberLocale()).format(Math.round(n || 0));
const mins = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "");

const CREDIT = {
  open: ["Credit open", "bg-emerald-500/15 text-emerald-200 border-emerald-500/40"],
  used: ["Credit used", "bg-sky-500/15 text-sky-200 border-sky-500/40"],
  expired: ["Credit expired", "bg-zinc-500/15 text-zinc-300 border-zinc-500/40"],
};

function Settings({ data, roster, onSaved }) {
  const [price, setPrice] = useState(data.settings.price);
  const [picked, setPicked] = useState(() => data.settings.coaches.map((c) => c.user_id));
  const [chargeTax, setChargeTax] = useState(data.settings.charge_tax);
  const [travel, setTravel] = useState(data.settings.travel_note || "");
  const [alerts, setAlerts] = useState((data.settings.alert_emails || []).join(", "));
  const [stack, setStack] = useState((data.settings.value_stack || []).map((r) => `${r.label} | ${r.value}`).join("\n"));
  const stackRows = () => stack.split("\n").map((l) => l.split("|")).filter((p) => p[0].trim())
    .map(([label, value]) => ({ label: label.trim(), value: Number((value || "").replace(/[^\d.]/g, "")) }));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async (enabled, msg) => {
    setBusy(true);
    try {
      await saveTrialWeekSettings({ enabled, price: Number(price), coach_ids: picked, charge_tax: chargeTax, travel_note: travel,
        alert_emails: alerts.split(/[\s,]+/).filter(Boolean), value_stack: stackRows() });
      toast.success(msg || (enabled ? "Trial week is on" : "Trial week is off")); onSaved();
    }
    catch (e) { toast.error(err(e, "Couldn't save")); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-4 flex flex-wrap items-end gap-3" data-testid="trial-settings">
      <label className="space-y-1">
        <span className="block text-xs text-zinc-400">Price before tax</span>
        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="trial-price"
          className="w-32 rounded-md bg-[#0d0d0d] border border-[#262626] px-3 py-2 text-sm text-white" />
      </label>
      <label className="flex items-center gap-2 pb-2 text-sm text-zinc-300">
        <input type="checkbox" checked={chargeTax} onChange={(e) => setChargeTax(e.target.checked)} data-testid="trial-charge-tax" />
        Add tax on top
      </label>
      <label className="min-w-[240px] flex-1 space-y-1">
        <span className="block text-xs text-zinc-400">How far the gym is (shown on the page)</span>
        <input value={travel} onChange={(e) => setTravel(e.target.value)} placeholder="About 10 minutes from Downtown Dubai" data-testid="trial-travel"
          className="w-full rounded-md bg-[#0d0d0d] border border-[#262626] px-3 py-2 text-sm text-white" />
      </label>
      <label className="w-full space-y-1">
        <span className="block text-xs text-zinc-400">
          &quot;Your week includes&quot; on the page: one per line, what it&apos;s worth after a |. Total shown struck through, then the price.
        </span>
        <textarea value={stack} onChange={(e) => setStack(e.target.value)} rows={4} data-testid="trial-stack"
          placeholder={"DIY nutrition course | 499\n1:1 accountability coach | 599"}
          className="w-full rounded-md bg-[#0d0d0d] border border-[#262626] px-3 py-2 font-mono text-xs text-white" />
      </label>
      <label className="min-w-[240px] flex-1 space-y-1">
        <span className="block text-xs text-zinc-400">Also email every signup to (the coach always gets it)</span>
        <input value={alerts} onChange={(e) => setAlerts(e.target.value)} placeholder="anish@liftr.fit, stephanie@liftr.fit" data-testid="trial-alerts"
          className="w-full rounded-md bg-[#0d0d0d] border border-[#262626] px-3 py-2 text-sm text-white" />
      </label>
      {data.settings.enabled ? (
        <>
          <button onClick={() => save(true)} disabled={busy} className="rounded-md border border-[#333] px-3 py-2 text-sm text-zinc-200">Save price</button>
          <button onClick={() => save(false)} disabled={busy} className="rounded-md border border-rose-500/40 px-3 py-2 text-sm text-rose-200" data-testid="trial-off">Switch off</button>
        </>
      ) : (
        <button onClick={() => save(true)} disabled={busy || !data.has_gateway} data-testid="trial-on"
          className="rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Switch on at this gym</button>
      )}
      {!data.has_gateway && <span className="text-xs text-amber-300">No online payments at this gym (Admin → Payments).</span>}
      <div className="w-full border-t border-[#262626] pt-3" data-testid="trial-coaches">
        <div className="text-xs text-zinc-400">
          Trial coaches. Each new trial goes to the next one in turn, skipping anyone on leave that week. Order = the order you tick them.
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {roster.map((c) => {
            const n = picked.indexOf(c.user_id);
            return (
              <button key={c.user_id} type="button" onClick={() => toggle(c.user_id)} aria-pressed={n >= 0}
                className={`rounded-full border px-3 py-1.5 text-xs ${n >= 0 ? "border-rose-500 bg-rose-500/15 text-white" : "border-[#2a2a2a] text-zinc-400"}`}>
                {n >= 0 ? `${n + 1}. ` : ""}{c.name}
              </button>
            );
          })}
          {!roster.length && <span className="text-xs text-zinc-500">No staff with the accountability coaching skill at this gym (User Management).</span>}
        </div>
        <button onClick={() => save(data.settings.enabled, "Trial coaches saved")} disabled={busy}
          className="mt-2 rounded-md border border-[#333] px-3 py-1.5 text-xs text-zinc-200" data-testid="trial-coaches-save">Save coaches</button>
      </div>
    </div>
  );
}

const TICKABLE = ["trainerize"];

function Row({ t, coaches, onSaved }) {
  const [coach, setCoach] = useState("");
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    setBusy(true);
    try { await setTrialCoach(t.id, coach); toast.success(`${coach} is ${t.name.split(" ")[0]}'s coach`); onSaved(); }
    catch (e) { toast.error(err(e, "Couldn't save")); }
    finally { setBusy(false); }
  };
  const called = async () => {
    setBusy(true);
    try { await markTrialCalled(t.id); onSaved(); }
    catch (e) { toast.error(err(e, "Couldn't save")); }
    finally { setBusy(false); }
  };
  const tick = async (step, done) => {
    setBusy(true);
    try { await tickTrialStep(t.id, step, done); onSaved(); }
    catch (e) { toast.error(err(e, "Couldn't save")); }
    finally { setBusy(false); }
  };
  const credit = t.credit && CREDIT[t.credit.status];
  return (
    <li className="card-surface p-4" data-testid={`trial-${t.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[180px] flex-1">
          <div className="font-semibold text-white">{t.name}</div>
          <div className="text-xs text-zinc-500">{t.phone} · {day(t.start_date)} to {day(t.end_date)}</div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {t.steps.map((s) => {
            const cls = `inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${s.done ? "border-emerald-500/40 text-emerald-200" : "border-[#262626] text-zinc-400"}`;
            const body = <>{s.done ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{s.title}{s.progress && !s.done ? ` (${s.progress})` : ""}</>;
            // Trainerize: the coach ticks it (tap again to undo)
            return TICKABLE.includes(s.key) ? (
              <button key={s.key} onClick={() => tick(s.key, !s.done)} disabled={busy} data-testid={`trial-tick-${s.key}-${t.id}`}
                title={s.done ? "Tap to undo" : "Tap when done"} className={`${cls} ${s.done ? "" : "hover:border-emerald-500/40 hover:text-emerald-200"}`}>
                {body}
              </button>
            ) : <span key={s.key} className={cls}>{body}</span>;
          })}
        </div>
        {credit && (
          <span className={`rounded-md border px-2 py-1 text-xs ${credit[1]}`}>
            {credit[0]}{t.credit.status === "open" ? ` until ${day(t.credit.expires_on)}` : ""}{t.credit.won ? " · won" : ""}
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <a href={`tel:${(t.phone || "").replace(/\s/g, "")}`} className="inline-flex items-center gap-1 rounded-md border border-[#333] px-2 py-1 text-xs text-zinc-200">
          <Phone className="h-3 w-3" />Call
        </a>
        {t.called_at ? (
          <span className="rounded-md bg-emerald-500/15 px-2 py-1 text-xs text-emerald-200">
            Called by {t.called_by || "coach"}{t.coach_set_at ? ` · ${mins(t.coach_set_at, t.called_at)} min after` : ""}
          </span>
        ) : (
          <button onClick={called} disabled={busy} data-testid={`trial-called-${t.id}`}
            className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">Called</button>
        )}
        {t.coach_name ? (
          <span className="text-zinc-300">Coach: <span className="text-white">{t.coach_name}</span></span>
        ) : (
          <>
            <span className="rounded-md bg-amber-500/15 px-2 py-1 text-xs text-amber-200">No coach yet</span>
            <select value={coach} onChange={(e) => setCoach(e.target.value)} data-testid={`trial-coach-${t.id}`}
              className="rounded-md bg-[#0d0d0d] border border-[#262626] px-2 py-1.5 text-sm text-white">
              <option value="">Pick a coach…</option>
              {coaches.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={assign} disabled={!coach || busy} className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {busy ? "Saving…" : "Assign"}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

export default function TrialWeek() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [roster, setRoster] = useState([]);
  const load = useCallback(() => getTrialWeek().then(setData).catch((e) => toast.error(err(e, "Couldn't load trials"))), []);
  useEffect(() => {
    load();
    listRoster("accountability").then((r) => setRoster(r || [])).catch(() => setRoster([]));
  }, [load, user?.active_facility_id]);
  if (!data) return <div className="p-8 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const link = data.page_path ? `${window.location.origin}${data.page_path}` : null;
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="trial-week-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-white flex items-center gap-2"><Sparkles className="h-5 w-5 text-rose-400" />Trial Week</h1>
        <p className="mt-1 max-w-3xl text-sm text-zinc-400">
          Bought online for {cur}{fmt(data.settings.price)}{data.settings.charge_tax ? " + tax" : ""}{data.rules.in_person ? "" : ", done in the app"}. Each new trial gets the next trial coach in turn,
          who gets a Discord message to call them right away: that call is the first time we talk to them. Press Called once you have.{" "}
          {data.rules.in_person ? (
            <>On that call, make sure they start the nutrition course online (the link is in their welcome email); the facility head adds them
            on Trainerize. They win the price back as credit for the 6 Week Challenge or 12 weeks+ by passing the nutrition test (ticks itself),
            training {data.rules.visits_needed} times (FPA check-ins) and replying to their coach on Trainerize: tap that step on their row once they have.</>
          ) : (
            <>They win the price back as credit for the 6 Week Challenge or 12 weeks+ by training {data.rules.visits_needed} times,
            finishing the nutrition course, setting their meal plan and replying to their coach.</>
          )}{" "}Not finishing still gets the credit
          after the week, with a softer message. Credit lasts {data.rules.credit_days} days after the week.
        </p>
      </div>
      {user?.role === "master_admin" && <Settings key={user?.active_facility_id} data={data} roster={roster} onSaved={load} />}
      {user?.role !== "master_admin" && data.settings.coaches.length > 0 && (
        <p className="text-sm text-zinc-400">Trial coaches, in turn: {data.settings.coaches.map((c) => c.name).join(", ")}</p>
      )}
      {data.settings.enabled && link && (
        <div className="card-surface p-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-zinc-400">Page for ads:</span>
          <a href={link} target="_blank" rel="noreferrer" className="font-mono text-rose-300 underline">{link}</a>
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} className="inline-flex items-center gap-1 rounded-md border border-[#333] px-2 py-1 text-xs text-zinc-200">
            <Copy className="h-3 w-3" />Copy
          </button>
        </div>
      )}
      {!data.settings.enabled && <p className="text-sm text-zinc-500">The trial week is off at this gym.</p>}
      {data.trials.length ? (
        <ul className="space-y-3">{data.trials.map((t) => <Row key={t.id} t={t} coaches={[...new Set(roster.map((c) => c.name).filter(Boolean))]} onSaved={load} />)}</ul>
      ) : data.settings.enabled ? <p className="text-sm text-zinc-500">No trials bought yet.</p> : null}
    </div>
  );
}
